import { useInfiniteScrollFromCache } from '@carboncopyco/data';
import type { BatchResponse } from '@carboncopyco/types';
import type { UseLazyQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import type { QueryDefinition } from '@reduxjs/toolkit/query';
import { useEffect, useMemo } from 'react';
import { useUrlState } from '../../utils';
import { INITIAL_URL_STATE } from '../consts';
import type { UrlState } from '../types';

type UseControlledFetchProps<R, A> = {
  lazyHook: UseLazyQuery<QueryDefinition<A, any, any, BatchResponse<R>>>;
  extraEndpointArgs: Partial<A>;
  mapUrlStateToEndpointArgs: Partial<Record<keyof UrlState, keyof A>>;
};

export function useControlledFetch<
  R extends Record<string, unknown>,
  A extends Record<any, any>,
>({
  lazyHook,
  extraEndpointArgs,
  mapUrlStateToEndpointArgs,
}: UseControlledFetchProps<R, A>) {
  const [urlState] = useUrlState<UrlState>(INITIAL_URL_STATE);

  const adaptedUrlState: Record<keyof UrlState, unknown> = {
    sort: urlState.sort,
    creators: urlState.creators?.split(','),
    companies: urlState.companies?.split(','),
    sources: urlState.sources?.split(','),
    sourceShareIds: urlState.sourceShareIds?.split(','),
    sourcePresentationIds: urlState.sourcePresentationIds?.split(','),
    activityType: urlState.activityType,
    query: urlState.query,
    active: urlState.active,
    links: urlState.links?.split(','),
    presenters: urlState.presenters?.split(','),
    opportunities: urlState.opportunities?.split(','),
    tab: urlState.tab,
  };

  const narrowedUrlStateArgs: Partial<A> = Object.fromEntries(
    Object.keys(mapUrlStateToEndpointArgs).map((urlKey) => [
      mapUrlStateToEndpointArgs[urlKey],
      adaptedUrlState[urlKey],
    ])
  );

  const fetchArgs = useMemo(
    () => ({
      ...narrowedUrlStateArgs,
      ...extraEndpointArgs,
    }),
    [urlState]
  );

  const {
    items,
    fetchFirstBatch,
    fetchNextBatch,
    isLoading,
    isLoadingBatch,
    error,
  } = useInfiniteScrollFromCache(lazyHook, fetchArgs as any);

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs]);

  return {
    items,
    fetchFirstBatch,
    fetchNextControlledBatch: () => fetchNextBatch(),
    isLoading,
    isLoadingBatch,
    error,
  };
}
